import { Assets } from "@/assets/images";
import { Loader } from "@/components";
import { useStore } from "@/store";
import { observer } from "mobx-react-lite";
import {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from "react";
import QRCode from "react-qr-code";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useHarmonicIntervalFn, useTimeoutFn } from "react-use";
import classes from "./SignIn.module.scss";

const INTERVAL = 1000;
const TIMEOUT = 60 * 1000;

export const SignIn: FunctionComponent = observer((): ReactElement => {
  const store = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [sessionUuid, updateSessionUuid] = useState<string | null>(null);

  const doGenerate = useCallback(async (): Promise<void> => {
    updateSessionUuid(await store.auth.createSession());
    reset();
  }, []);

  const [, , reset] = useTimeoutFn(doGenerate, TIMEOUT);

  useHarmonicIntervalFn(async (): Promise<void> => {
    if (null === sessionUuid) {
      return;
    }

    try {
      const { token, refreshToken } = await store.auth.readSession(sessionUuid);
      await store.auth.duplicateSession(token, refreshToken);
      await store.app.healthCheck();
      await store.app.load();

      navigate(location.state?.returnUrl || "/", { replace: true });
    } catch (err) {
      console.error(err);
    }
  }, INTERVAL);

  useEffect((): (() => void) => {
    document.body.classList.add(classes.authPage);

    if (store.app.logged) {
      navigate(location.state?.returnUrl || "/", { replace: true });
    } else {
      doGenerate();
    }

    return () => {
      document.body.classList.remove(classes.authPage);
    };
  }, []);

  return (
    <div className={classes.auth}>
      <Link to="/">
        <Assets.LogoSvg className={classes.logo} />
      </Link>
      {null === sessionUuid ? (
        <Loader className={classes.loader} />
      ) : (
        <QRCode size={300} value={sessionUuid} />
      )}
    </div>
  );
});
